import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { authApi } from '@/services/api'
import { toast } from 'sonner'
import { Mail, Lock, ArrowLeft, Loader2, CheckCircle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

export function ForgotPasswordPage() {
    const [step, setStep] = useState(1) // 1: Email, 2: OTP, 3: New Password
    const [email, setEmail] = useState('')
    const [otp, setOtp] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const navigate = useNavigate()

    const handleRequestOTP = async (e) => {
        e.preventDefault()

        if (!email) {
            toast.error('Please enter your email')
            return
        }

        setLoading(true)
        try {
            await authApi.requestPasswordReset(email)
            toast.success('Password reset code sent to your email')
            setStep(2)
        } catch (error) {
            console.error('Request OTP error:', error)
            toast.error(error.response?.data?.message || 'Failed to send reset code')
        } finally {
            setLoading(false)
        }
    }

    const handleVerifyOTP = async (e) => {
        e.preventDefault()

        if (!otp) {
            toast.error('Please enter the verification code')
            return
        }

        setLoading(true)
        try {
            await authApi.verifyResetOTP(email, otp)
            toast.success('Code verified successfully')
            setStep(3)
        } catch (error) {
            console.error('Verify OTP error:', error)
            toast.error(error.response?.data?.message || 'Invalid or expired code')
        } finally {
            setLoading(false)
        }
    }

    const handleResetPassword = async (e) => {
        e.preventDefault()

        if (!newPassword || !confirmPassword) {
            toast.error('Please fill in all fields')
            return
        }

        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match')
            return
        }

        if (newPassword.length < 10) {
            toast.error('Password must be at least 10 characters')
            return
        }

        setLoading(true)
        try {
            await authApi.resetPassword(email, otp, newPassword)
            toast.success('Password reset successfully!')
            setTimeout(() => navigate('/login'), 2000)
        } catch (error) {
            console.error('Reset password error:', error)
            const errors = error.response?.data?.errors
            if (errors && Array.isArray(errors)) {
                errors.forEach(err => toast.error(err))
            } else {
                toast.error(error.response?.data?.message || 'Failed to reset password')
            }
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-50 flex items-center justify-center px-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8"
            >
                {/* Logo */}
                <div className="flex justify-center mb-8">
                    <div data-logo-box className="w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-300">
                        <span className="text-white font-bold text-2xl">N</span>
                    </div>
                </div>

                {/* Header */}
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        {step === 1 && 'Forgot Password'}
                        {step === 2 && 'Verify Code'}
                        {step === 3 && 'Reset Password'}
                    </h1>
                    <p className="text-gray-600">
                        {step === 1 && 'Enter your email to receive a reset code'}
                        {step === 2 && 'Enter the 6-digit code sent to your email'}
                        {step === 3 && 'Create a new password for your account'}
                    </p>
                </div>

                <AnimatePresence mode="wait">
                    {/* Step 1: Email */}
                    {step === 1 && (
                        <motion.form
                            key="email-form"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            onSubmit={handleRequestOTP}
                            className="space-y-4"
                        >
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Email Address
                                </label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                        placeholder="you@example.com"
                                        disabled={loading}
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <Button
                                type="submit"
                                className="w-full"
                                size="lg"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Sending code...
                                    </>
                                ) : (
                                    'Send Reset Code'
                                )}
                            </Button>
                        </motion.form>
                    )}

                    {/* Step 2: OTP */}
                    {step === 2 && (
                        <motion.form
                            key="otp-form"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            onSubmit={handleVerifyOTP}
                            className="space-y-4"
                        >
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Verification Code
                                </label>
                                <input
                                    type="text"
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-center text-2xl tracking-widest font-mono"
                                    placeholder="000000"
                                    maxLength={6}
                                    disabled={loading}
                                    autoFocus
                                />
                                <p className="text-xs text-gray-500 mt-2 text-center">
                                    Code sent to {email}
                                </p>
                            </div>

                            <Button
                                type="submit"
                                className="w-full"
                                size="lg"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Verifying...
                                    </>
                                ) : (
                                    'Verify Code'
                                )}
                            </Button>

                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                className="w-full text-sm text-gray-600 hover:text-gray-900"
                            >
                                Use a different email
                            </button>

                            <button
                                type="button"
                                onClick={handleRequestOTP}
                                disabled={loading}
                                className="w-full text-sm text-primary hover:text-primary/80 font-medium disabled:opacity-50"
                            >
                                {loading ? 'Sending...' : 'Resend code'}
                            </button>
                        </motion.form>
                    )}

                    {/* Step 3: New Password */}
                    {step === 3 && (
                        <motion.form
                            key="password-form"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            onSubmit={handleResetPassword}
                            className="space-y-4"
                        >
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    New Password
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                        placeholder="••••••••"
                                        disabled={loading}
                                        autoFocus
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">Must be at least 12 characters</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Confirm Password
                                </label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                                        placeholder="••••••••"
                                        disabled={loading}
                                    />
                                </div>
                            </div>

                            <Button
                                type="submit"
                                className="w-full"
                                size="lg"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Resetting password...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                        Reset Password
                                    </>
                                )}
                            </Button>
                        </motion.form>
                    )}
                </AnimatePresence>

                {/* Back to login */}
                <div className="mt-6 text-center">
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Login
                    </Link>
                </div>
            </motion.div>
        </div>
    )
}
